/**
 * GAMIFICATION SYSTEM CONSTANTS
 *
 * This file defines all the gamification rules, achievements, levels, and point calculations
 * for the VetriaAI job seeker platform.
 */

// ============ LEVEL SYSTEM ============

export const LEVELS = [
  {
    level: 1,
    name: 'Explorer',
    emoji: '🌱',
    minXP: 0,
    maxXP: 100,
    color: 'from-green-400 to-green-600',
    description: 'Just starting your journey',
    testsRequired: 1
  },
  {
    level: 2,
    name: 'Explorer',
    emoji: '🌱',
    minXP: 100,
    maxXP: 250,
    color: 'from-green-400 to-green-600',
    description: 'Learning the basics',
    testsRequired: 5
  },
  {
    level: 3,
    name: 'Challenger',
    emoji: '⚡',
    minXP: 250,
    maxXP: 500,
    color: 'from-blue-400 to-blue-600',
    description: 'Actively engaging',
    testsRequired: 6
  },
  {
    level: 4,
    name: 'Challenger',
    emoji: '⚡',
    minXP: 500,
    maxXP: 1000,
    color: 'from-blue-400 to-blue-600',
    description: 'Building momentum',
    testsRequired: 15
  },
  {
    level: 5,
    name: 'Champion',
    emoji: '🔥',
    minXP: 1000,
    maxXP: 2000,
    color: 'from-orange-400 to-red-600',
    description: 'Consistently performing',
    testsRequired: 16
  },
  {
    level: 6,
    name: 'Champion',
    emoji: '🔥',
    minXP: 2000,
    maxXP: 3500,
    color: 'from-orange-400 to-red-600',
    description: 'High activity level',
    testsRequired: 30
  },
  {
    level: 7,
    name: 'Master',
    emoji: '💎',
    minXP: 3500,
    maxXP: 5500,
    color: 'from-purple-400 to-purple-600',
    description: 'Platform expert',
    testsRequired: 31
  },
  {
    level: 8,
    name: 'Master',
    emoji: '💎',
    minXP: 5500,
    maxXP: 8000,
    color: 'from-purple-400 to-purple-600',
    description: 'Top performer',
    testsRequired: 50
  },
  {
    level: 9,
    name: 'Legend',
    emoji: '👑',
    minXP: 8000,
    maxXP: 12000,
    color: 'from-yellow-400 to-yellow-600',
    description: 'Elite status',
    testsRequired: 51
  },
  {
    level: 10,
    name: 'Legend',
    emoji: '👑',
    minXP: 12000,
    maxXP: 999999,
    color: 'from-yellow-400 to-yellow-600',
    description: 'Community leader',
    testsRequired: 100
  }
];

// ============ POINT SYSTEM ============

export const POINTS = {
  // Daily Activity - REMOVED (no points for daily login/streak)
  DAILY_LOGIN: 0,
  DAILY_STREAK: 0,

  // Test Completion (only if score ≥ 60%)
  TEST_COMPLETED: 5, // Pass any HR test
  TEST_HIGH_SCORE_90: 5, // Bonus for 90%+

  // Skills Validation Test (FIRST TIME ONLY)
  SKILLS_VALIDATION_FIRST_TIME: 10, // Only awarded once

  // Streak Milestones (achievements only, not daily)
  STREAK_7_DAYS: 25,
  STREAK_14_DAYS: 50,
  STREAK_30_DAYS: 150,
  STREAK_60_DAYS: 350,
  STREAK_100_DAYS: 750,
  STREAK_365_DAYS: 5000,

  // Learning
  COURSE_COMPLETED: 0, // No points for just completing
  COURSE_QUIZ_PASSED: 5, // Pass course test
  COURSE_SPEED_BONUS: 0, // Removed

  // Practice - REMOVED (no points for now)
  PRACTICE_INTERVIEW: 0,
  PRACTICE_INTERVIEW_HIGH_SCORE: 0,

  // Profile Verification (30 points total breakdown)
  LOGIN_AND_RESUME: 5, // Combined: first login + resume upload
  PASS_SKILLS_TEST: 10, // Pass skills validation test
  PASS_COURSE_TEST: 10, // Pass course test
  VERIFICATION_BADGE: 5, // 100% verification complete

  // HR Interaction
  HR_VIEWED_PROFILE: 0, // Removed
  SHORTLISTED: 10, // HR shortlists jobseeker

  // Referral - REMOVED (no points for now)
  REFERRAL_SIGNUP: 0,
  REFERRAL_VERIFIED: 0,
};

// ============ ACHIEVEMENT DEFINITIONS ============

export const ACHIEVEMENTS = [
  // ========== TEST MASTERY ==========
  {
    id: 'first_steps',
    title: 'First Steps',
    description: 'Complete your first test',
    emoji: '🎯',
    category: 'test_mastery',
    points: 25, // 50 → 25
    requirement: { type: 'test_count', value: 1 },
    rarity: 'common'
  },
  {
    id: 'test_veteran',
    title: 'Test Veteran',
    description: 'Complete 10 tests',
    emoji: '🔥',
    category: 'test_mastery',
    points: 50, // 100 → 50
    requirement: { type: 'test_count', value: 10 },
    rarity: 'uncommon'
  },
  {
    id: 'century_club',
    title: 'Century Club',
    description: 'Complete 100 tests',
    emoji: '💯',
    category: 'test_mastery',
    points: 500, // 1000 → 500
    requirement: { type: 'test_count', value: 100 },
    rarity: 'legendary'
  },
  {
    id: 'perfect_score',
    title: 'Perfect Score',
    description: 'Score 100% on any test',
    emoji: '⭐',
    category: 'test_mastery',
    points: 75, // 150 → 75
    requirement: { type: 'test_score', value: 100 },
    rarity: 'rare'
  },
  {
    id: 'knowledge_hunter',
    title: 'Knowledge Hunter',
    description: 'Complete 5 tests in 5 different skill categories',
    emoji: '🎓',
    category: 'test_mastery',
    points: 100, // 200 → 100
    requirement: { type: 'special', value: 5, extra: { type: 'diverse_skills' } },
    rarity: 'rare'
  },
  {
    id: 'quick_learner',
    title: 'Quick Learner',
    description: 'Complete 3 tests in 24 hours',
    emoji: '🚀',
    category: 'test_mastery',
    points: 75, // 150 → 75
    requirement: { type: 'special', value: 3, extra: { type: 'time_window', hours: 24 } },
    rarity: 'uncommon'
  },
  {
    id: 'night_owl',
    title: 'Night Owl',
    description: 'Take test between 12 AM - 6 AM',
    emoji: '🌙',
    category: 'test_mastery',
    points: 25, // 50 → 25
    requirement: { type: 'time', value: 0, extra: { startHour: 0, endHour: 6 } },
    rarity: 'uncommon'
  },
  // REMOVED: Early Bird
  // REMOVED: Speed Demon

  // ========== PERFORMANCE ==========
  {
    id: 'bronze_standard',
    title: 'Bronze Standard',
    description: 'Score 60%+ on 5 tests',
    emoji: '🥉',
    category: 'performance',
    points: 50, // 100 → 50
    requirement: { type: 'test_score', value: 60, extra: { count: 5 } },
    rarity: 'common'
  },
  {
    id: 'silver_standard',
    title: 'Silver Standard',
    description: 'Score 75%+ on 10 tests',
    emoji: '🥈',
    category: 'performance',
    points: 100, // 200 → 100
    requirement: { type: 'test_score', value: 75, extra: { count: 10 } },
    rarity: 'uncommon'
  },
  {
    id: 'gold_standard',
    title: 'Gold Standard',
    description: 'Score 90%+ on 15 tests',
    emoji: '🥇',
    category: 'performance',
    points: 250, // 500 → 250
    requirement: { type: 'test_score', value: 90, extra: { count: 15 } },
    rarity: 'rare'
  },
  {
    id: 'diamond_elite',
    title: 'Diamond Elite',
    description: 'Score 95%+ on 20 tests',
    emoji: '💎',
    category: 'performance',
    points: 500, // 1000 → 500
    requirement: { type: 'test_score', value: 95, extra: { count: 20 } },
    rarity: 'epic'
  },
  {
    id: 'consistent_performer',
    title: 'Consistent Performer',
    description: 'Score 80%+ on 10 consecutive tests',
    emoji: '🎯',
    category: 'performance',
    points: 150, // 300 → 150
    requirement: { type: 'special', value: 80, extra: { type: 'consecutive', count: 10 } },
    rarity: 'rare'
  },
  {
    id: 'rising_star',
    title: 'Rising Star',
    description: 'Improve score by 20% over 5 tests',
    emoji: '📈',
    category: 'performance',
    points: 100, // 200 → 100
    requirement: { type: 'special', value: 20, extra: { type: 'improvement', count: 5 } },
    rarity: 'uncommon'
  },

  // ========== ENGAGEMENT ==========
  {
    id: 'streak_7_days',
    title: '7-Day Streak',
    description: 'Active for 7 consecutive days',
    emoji: '🔥',
    category: 'engagement',
    points: 25, // 50 → 25
    requirement: { type: 'streak', value: 7 },
    rarity: 'common'
  },
  {
    id: 'streak_14_days',
    title: '14-Day Streak',
    description: 'Active for 14 consecutive days',
    emoji: '🔥',
    category: 'engagement',
    points: 50, // NEW
    requirement: { type: 'streak', value: 14 },
    rarity: 'common'
  },
  {
    id: 'streak_30_days',
    title: '30-Day Warrior',
    description: 'Active for 30 consecutive days',
    emoji: '⚡',
    category: 'engagement',
    points: 150, // 300 → 150
    requirement: { type: 'streak', value: 30 },
    rarity: 'rare'
  },
  {
    id: 'streak_60_days',
    title: '60-Day Champion',
    description: 'Active for 60 consecutive days',
    emoji: '💪',
    category: 'engagement',
    points: 350, // NEW (700 → 350)
    requirement: { type: 'streak', value: 60 },
    rarity: 'rare'
  },
  {
    id: 'streak_100_days',
    title: '100-Day Legend',
    description: 'Active for 100 consecutive days',
    emoji: '👑',
    category: 'engagement',
    points: 750, // 1500 → 750
    requirement: { type: 'streak', value: 100 },
    rarity: 'legendary'
  },
  {
    id: 'streak_365_days',
    title: 'Year-Long Dedication',
    description: 'Active for 365 consecutive days',
    emoji: '🏆',
    category: 'engagement',
    points: 5000, // 10000 → 5000
    requirement: { type: 'streak', value: 365 },
    rarity: 'legendary'
  },
  {
    id: 'profile_master',
    title: 'Profile Master',
    description: 'Complete 100% of profile',
    emoji: '🎨',
    category: 'engagement',
    points: 50, // 100 → 50
    requirement: { type: 'profile', value: 100 },
    rarity: 'common'
  },
  {
    id: 'learning_champion',
    title: 'Learning Champion',
    description: 'Complete 5 skill improvement courses',
    emoji: '📚',
    category: 'engagement',
    points: 150, // 300 → 150
    requirement: { type: 'special', value: 5, extra: { type: 'courses' } },
    rarity: 'uncommon'
  },
  {
    id: 'interview_pro',
    title: 'Interview Pro',
    description: 'Complete 10 AI interviews with 80%+ scores',
    emoji: '🎤',
    category: 'engagement',
    points: 200, // 400 → 200
    requirement: { type: 'special', value: 10, extra: { type: 'interviews', minScore: 80 } },
    rarity: 'rare'
  },

  // ========== SPECIAL ==========
  // REMOVED: Beta Tester
  {
    id: 'unicorn_hunter',
    title: 'Unicorn Hunter',
    description: 'Get shortlisted by 10 different companies',
    emoji: '🦄',
    category: 'special',
    points: 250, // 500 → 250
    requirement: { type: 'special', value: 10, extra: { type: 'shortlisted' } },
    rarity: 'epic'
  },
  {
    id: 'job_magnet',
    title: 'Job Magnet',
    description: 'Get shortlisted by 20 different companies',
    emoji: '💼',
    category: 'special',
    points: 300, // 600 → 300 (changed from invitations to shortlisted)
    requirement: { type: 'special', value: 20, extra: { type: 'shortlisted' } },
    rarity: 'epic'
  },
  // REMOVED: Career Champion
];

// ============ HELPER FUNCTIONS ============

export function getLevelByXP(xp) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].minXP) {
      return LEVELS[i];
    }
  }
  return LEVELS[0];
}

export function getXPToNextLevel(currentXP) {
  const currentLevel = getLevelByXP(currentXP);
  const nextLevel = LEVELS.find(l => l.level === currentLevel.level + 1);
  if (!nextLevel) return 0; // Max level reached
  return nextLevel.minXP - currentXP;
}

export function calculateStreakPoints(streakDays) {
  // NO daily streak points, only milestone achievements
  // Streaks are now handled through achievements only
  return 0;
}

export function calculateTestPoints(score, testCount) {
  // Only award points if test is passed (≥60%)
  if (score < 60) {
    return 0;
  }

  let points = POINTS.TEST_COMPLETED; // 5 points for passing

  // Bonus for 90%+ score
  if (score >= 90) {
    points += POINTS.TEST_HIGH_SCORE_90; // +5 points
  }

  return points;
}

export function getRarityColor(rarity) {
  switch (rarity) {
    case 'common': return 'text-gray-600 bg-gray-100';
    case 'uncommon': return 'text-green-600 bg-green-100';
    case 'rare': return 'text-blue-600 bg-blue-100';
    case 'epic': return 'text-purple-600 bg-purple-100';
    case 'legendary': return 'text-yellow-600 bg-yellow-100';
    default: return 'text-gray-600 bg-gray-100';
  }
}

export function getAchievementsByCategory(category) {
  return ACHIEVEMENTS.filter(a => a.category === category);
}

